import { lanzamientoAlimentec, lanzamientoPanettone } from "./images";
import styled from "@emotion/styled";
import { Button } from "@mui/material";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { useEffect, useRef, useState } from "react";

const AUTOPLAY_MS = 6000;
const SWIPE_MIN_PX = 50;

// Mismos cortes que los <source> del <picture>. Los rangos no se solapan, así
// que lo que se configure en un tamaño no afecta a los demás.
const BREAKPOINTS = {
  hd: "@media (min-width: 1367px)",
  standard: "@media (min-width: 1025px) and (max-width: 1366px)",
  tablet: "@media (min-width: 769px) and (max-width: 1024px)",
  mobile: "@media (max-width: 768px)",
};

// Botón por defecto. Cada campaña puede sobrescribir, por tamaño de banner,
// la posición (position) y el tamaño del botón (size).
const DEFAULT_BUTTON = {
  text: "PARTICIPAR",
  position: {
    hd: { top: "75%", left: "9%" },
    standard: { top: "70%", left: "8%" },
    tablet: { top: "75%", left: "6%" },
    // En mobile el botón queda centrado sobre el borde inferior del banner.
    mobile: {
      top: "calc(100% - 28px)",
      left: "50%",
      transform: "translate(-50%, -50%)",
    },
  },
  size: {
    hd: { fontSize: "1.3rem", padding: "8px 32px" },
    standard: { fontSize: "1.3rem", padding: "8px 32px" },
    tablet: { fontSize: "1.2rem", padding: "8px 28px" },
    mobile: { fontSize: "1.1rem !important", padding: "6px 24px" },
  },
};

// Campañas del banner del home. Cada campaña con active: true es un slide;
// pon active: false para ocultarla sin borrarla.
const campaigns = [
  {
    active: true,
    banners: lanzamientoAlimentec,
    alt: "Lanzamiento Delicake",
    href: "/delicake",
  },
  {
    active: true,
    banners: lanzamientoPanettone,
    alt: "Lanzamiento Panettone",
    href: "/panettone",
    // TODO: ajustar la posición al diseño final de los banners de Panettone.
    button: {
      position: {
        hd: { top: "70%", left: "9%" },
        standard: { top: "65%", left: "8%" },
        tablet: { top: "50%", left: "6%" },
        mobile: { top: "85%", left: "50%" },
      },
    },
  },
];

// Combina el botón de la campaña con el de por defecto, tamaño por tamaño.
const mergeButton = (button = {}) => {
  const mergeByBreakpoint = (key) =>
    Object.fromEntries(
      Object.keys(BREAKPOINTS).map((bp) => [
        bp,
        { ...DEFAULT_BUTTON[key][bp], ...button[key]?.[bp] },
      ]),
    );
  return {
    text: button.text ?? DEFAULT_BUTTON.text,
    position: mergeByBreakpoint("position"),
    size: mergeByBreakpoint("size"),
  };
};

const toMediaQueries = (stylesByBreakpoint) =>
  Object.fromEntries(
    Object.entries(BREAKPOINTS).map(([bp, query]) => [
      query,
      stylesByBreakpoint[bp],
    ]),
  );

const slides = campaigns
  .filter((campaign) => campaign.active)
  .map((campaign) => ({ ...campaign, button: mergeButton(campaign.button) }));
const hasMultipleSlides = slides.length > 1;

export const LanzamientoBanner = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const goTo = (index) =>
    setCurrent((index + slides.length) % slides.length);

  useEffect(() => {
    if (paused || !hasMultipleSlides) return;
    const interval = setInterval(
      () => setCurrent((prev) => (prev + 1) % slides.length),
      AUTOPLAY_MS,
    );
    return () => clearInterval(interval);
  }, [paused, current]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || !hasMultipleSlides) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > SWIPE_MIN_PX) goTo(current + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  if (slides.length === 0) return null;

  return (
    <Container
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="banner-viewport">
        <div
          className="banner-track"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.href}
              className="banner-slide"
              aria-hidden={i !== current}
            >
              <picture>
                <source
                  media="(max-width: 768px)"
                  srcSet={slide.banners.mobile}
                />
                <source
                  media="(max-width: 1024px)"
                  srcSet={slide.banners.tablet}
                />
                <source
                  media="(max-width: 1366px)"
                  srcSet={slide.banners.standard}
                />
                <img src={slide.banners.hd} alt={slide.alt} />
              </picture>
              <StyledButtonLink
                href={slide.href}
                target="blank"
                position={slide.button.position}
                tabIndex={i === current ? 0 : -1}
              >
                <Button
                  sx={{
                    zIndex: 100,
                    ...toMediaQueries(slide.button.size),
                  }}
                  color="secondary"
                  variant="contained"
                  tabIndex={-1}
                >
                  {slide.button.text}
                </Button>
              </StyledButtonLink>
            </div>
          ))}
        </div>

        {hasMultipleSlides && (
          <>
            <button
              type="button"
              className="banner-arrow banner-arrow--prev"
              aria-label="Banner anterior"
              onClick={() => goTo(current - 1)}
            >
              <ArrowBackIosRoundedIcon />
            </button>
            <button
              type="button"
              className="banner-arrow banner-arrow--next"
              aria-label="Banner siguiente"
              onClick={() => goTo(current + 1)}
            >
              <ArrowForwardIosRoundedIcon />
            </button>
          </>
        )}
      </div>

      {hasMultipleSlides && (
        <div className="banner-dots">
          {slides.map((slide, i) => (
            <button
              key={slide.href}
              type="button"
              className={i === current ? "active" : ""}
              aria-label={`Ir al banner ${i + 1}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </Container>
  );
};

const StyledButtonLink = styled("a", {
  shouldForwardProp: (prop) => prop !== "position",
})(({ position }) => ({
  position: "absolute",
  zIndex: 1,
  ...toMediaQueries(position),
}));

const Container = styled.div`
  margin-top: 100px;
  @media (max-width: 768px) {
    margin-top: 70px;
  }

  .banner-viewport {
    width: 100%;
    margin-inline: auto;
    position: relative;
    overflow: hidden;
  }

  .banner-track {
    display: flex;
    transition: transform 0.5s ease;
  }

  .banner-slide {
    flex: 0 0 100%;
    position: relative;

    /* En mobile el botón por defecto queda centrado sobre el borde inferior
       del banner, así que dejamos espacio para que el overflow del slider no
       lo corte. */
    @media (max-width: 768px) {
      padding-bottom: 28px;
    }

    picture {
      display: block;
      width: 100%;
    }

    img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }
  }

  .banner-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.7);
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #fff;
    }

    &--prev {
      left: 16px;
    }

    &--next {
      right: 16px;
    }

    @media (max-width: 768px) {
      width: 36px;
      height: 36px;

      svg {
        font-size: 1.1rem;
      }

      &--prev {
        left: 8px;
      }

      &--next {
        right: 8px;
      }
    }
  }

  .banner-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 12px 0;

    button {
      width: 10px;
      height: 10px;
      padding: 0;
      border: none;
      border-radius: 50%;
      background: #c4c4c4;
      cursor: pointer;
      transition: background 0.2s, transform 0.2s;

      &.active {
        background: #773d1e;
        transform: scale(1.2);
      }
    }
  }
`;
